import { AlertTriangle, Link2 } from 'lucide-react';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup, useGameGroupsStore } from '@/stores/gameGroups.store';
import { joinGroupLabels } from '@/utils/supervision';
import styles from './GroupNotices.module.scss';

/**
 * Erreur de lecture d'une page de supervision, nommée par le groupe de jeu consulté : sans elle, un
 * satellite Iris injoignable donnait des compteurs à zéro, lus comme « aucun joueur sur Iris ».
 * Ne rend RIEN sans groupe actif (Paladium) : ses pages restent exactement comme avant.
 */
export function GroupFetchError({ error, onRetry }: { error: string | null; onRetry?: () => void }) {
  const group = useActiveGameGroup();
  if (!group || !error) return null;
  return (
    <div className={styles.error} role="alert">
      <AlertTriangle size={16} className={styles.errorIcon} />
      <GroupBadge group={group} size="sm" title={`Groupe de jeu : ${group.label}`} />
      <span className={styles.errorText}>Données indisponibles : {error}</span>
      {onRetry && (
        <button type="button" className={styles.retry} onClick={onRetry}>Réessayer</button>
      )}
    </div>
  );
}

/**
 * Donnée commune à tous les groupes (les métiers : base Jobs partagée) affichée sous un groupe : la
 * page le dit, pour qu'on ne lise pas l'XP d'Iris seul. Rien sans groupe actif, ni pour un seul groupe.
 *
 * @param subject ce qui est commun, en tête de phrase (« Métiers »)
 * @param consequence ce que cela implique pour les chiffres affichés
 */
export function SharedAcrossGroupsNote({ subject, consequence }: { subject: string; consequence: string }) {
  const group = useActiveGameGroup();
  const gameId = useGameStore((s) => s.activeGameId);
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  if (!group || !entry || entry.groups.length < 2) return null;
  const labels = joinGroupLabels(entry.groups.map((g) => g.label));
  return (
    <div className={styles.shared} role="note">
      <Link2 size={16} className={styles.sharedIcon} />
      <span>
        <strong>{subject} communs à {labels}</strong> : {consequence}
      </span>
    </div>
  );
}
